import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import * as dates from '@simplybenefits/sb-dates';
import { i18n, tokens, localizationMaps, Loader } from '@simplybenefits/sb-ui-library';
import MetaClaimInputModal from './MetaClaimInputModal.js';
import SubmitReceiptModal from './SubmitReceiptModal.js';
import AddSupportingDocsModal from './AddSupportingDocsModal.js';
import ReviewClaimModal from './ReviewClaimModal.js';
import ClaimSuccessModal from './ClaimSuccessModal.js';
import ClaimProcessingModal from './ClaimProcessingModal.js';
import ClaimErrorModal from './ClaimErrorModal.js';
import HealthSpendingModal from '../../SpendingAccounts/HealthSpendingModal.js';
import { submitClaim, sendAutoAdjudicationEmail } from '../../../actions/claims.js';
import { getUsage } from '../../../actions/user.js';
import { HSApayment } from '../../../actions/spendingAccounts.js';

class SubmitClaim extends PureComponent {
    state = {
        currentPage: 0,
        numPages: 5,
        meta: null,
        receiptPhoto: null,
        supportingPhotos: null,
        showSuccess: false,
        showHSA: false,
        claimID: null,
        uid: null,
        fixableClaimErrors: [],
        isProcessingTopOff: false,
        showClaimProcessingScreen: false
    };

    _next = () => {
        let index = this.state.currentPage + 1;

        if (index > this.state.numPages - 1) {
            index = this.state.numPages - 1;
        }

        this.setState({
            currentPage: index
        });
    };

    _previous = () => {
        let index = this.state.currentPage - 1;

        if (index < 0) {
            index = 0;
            return;
        }

        this.setState({
            currentPage: index
        });
    };

    _handleCancel = () => {
        const cancel = window.confirm(i18n.t(tokens.labels.claims.areYouSureYouWantToCancelTheClaim));

        if (cancel) {
            this.props.close();
        }
    };

    /**
     * When each child view is completed we call this method to save the data in state
     * @param data
     * @param type
     * @private
     */
    _saveDataInState = (data, type) => {
        this.setState({
            [type]: data
        });
    };

    _submitClaim = async (continueAnyway = false) => {
        this.setState({
            showClaimProcessingScreen: true
        });

        const { patient } = this.state.meta;

        const isDep = [
            i18n.t(tokens.dropdowns.relationships.spouse),
            i18n.t(tokens.dropdowns.relationships["common law"]),
            i18n.t(tokens.dropdowns.relationships.child) ]
            .some((rel) => patient.name.includes(`(${ rel })`));

        const patientName = isDep ? patient.name.split(' (')[0] : patient.name;
        const patientType = isDep ? 'dependent' : 'primary';
        const dependentID = isDep ? patient.id : null;

        // create a Form Data form for posting the images and meta data to the server
        const form = new FormData();
        form.append('patient', patientName);
        form.append('patientType', patientType);
        form.append('dependentID', dependentID);
        form.append('category', localizationMaps.toEnglish("categories", this.state.meta.category));
        form.append('service', localizationMaps.toEnglish("benefits", this.state.meta.service));
        form.append('amountSubmitted', this.state.meta.amountSubmitted);
        form.append('serviceDate', dates.getUTC(this.state.meta.serviceDate));

        form.append("docs", null); // first entry must be null to fix CORS issue with Chrome (I know it's nuts)
        form.append("docs", this.state.receiptPhoto.file); // second entry is the receipt

        // attach each supporting document to the supporting docs array
        this.state.supportingPhotos && this.state.supportingPhotos.map((item) => {
            form.append("docs", item.file);
            return true;
        });

        if (this.props.type === 'Lifestyle') {
            form.append('claimType', 'lsa');
        } else if (this.props.type === 'HealthSpending') {
            form.append('claimType', 'hsa');
        } else {
            form.append('claimType', 'standard');
        }

        form.append('continueAnyway', Boolean(continueAnyway));

        const claim = await this.props.dispatch(submitClaim(form, this.props.type));

        if (claim) {
            if (claim.fixableClaimErrors) {
                this.setState({
                    fixableClaimErrors: claim.fixableClaimErrors,
                    showClaimProcessingScreen: false
                });

                return;
            }

            const amountClaimed = claim.amountPaid.toFixed(2);
            const amountSubmitted = claim.amountSubmitted.toFixed(2);

            if (this.props.type === 'Lifestyle' || this.props.type === 'HealthSpending') {
                this.setState({
                    showSuccess: true,
                    showHSA: false,
                    amountClaimed,
                    amountSubmitted,
                    claimID: claim.claimID,
                    uid: claim.uid,
                    fixableClaimErrors: [],
                    showClaimProcessingScreen: false
                });

                this.props.dispatch(sendAutoAdjudicationEmail(this.state.claimID));
                return;
            }

            await this.props.dispatch(getUsage(null, true));

            const isDependent = this.props.meta.isDependent;

            let showHSA = false;

            // if they have spending accounts, are eligible and have HSA then set the showHSA = true
            if (this.props.coverage.spendingAccounts && this.props.coverage.spendingAccounts.meta.benefitActive && this.props.coverage.spendingAccounts.meta.isEligible && Boolean(this.props.coverage.spendingAccounts.health.hasBenefit)) {
                showHSA = (Number(amountClaimed) < Number(amountSubmitted)) && Number(this.props.healthSpendRemaining) > 0 && !isDependent;
            }

            // don't show the HSA top off for dental or drugs
            if (localizationMaps.toEnglish("categories", this.state.meta.category).toUpperCase() === 'DENTAL' || localizationMaps.toEnglish("categories", this.state.meta.category).toUpperCase() === 'DRUGS') {
                showHSA = false;
            }

            if (claim.status === 'declined') {
                showHSA = false;
                this.props.dispatch(sendAutoAdjudicationEmail(claim.claimID));
            }

            this.setState({
                showSuccess: !showHSA,
                showHSA,
                amountClaimed: amountClaimed,
                amountSubmitted: amountSubmitted,
                claimID: claim.claimID,
                uid: claim.uid,
                fixableClaimErrors: [],
                showClaimProcessingScreen: false
            });
        } else {
            this.setState({
                showClaimProcessingScreen: false,
                currentPage: 3
            });
        }
    };
    
    _useHSA = async () => {
        // disable hsa top off button to prevent stacking top offs
        await this.setState({
            isProcessingTopOff: true
        });

        const amountUsed = await this.props.dispatch(HSApayment(this.state.claimID));
        const amountClaimed = Number(this.state.amountClaimed) + amountUsed;

        this._showSuccess(amountClaimed);
    };
    
    _showSuccess = (amountClaimed = false) => {
        const data = {
            showHSA: false,
            showSuccess: true
        };
        
        if (amountClaimed) {
            data.amountClaimed = amountClaimed;
        }

        this.props.dispatch(sendAutoAdjudicationEmail(this.state.claimID));     

        this.setState({ ...data });
    };

    render() {
        if (this.state.fixableClaimErrors && this.state.fixableClaimErrors.length > 0) {
            return (
                <ClaimErrorModal
                    fixableClaimErrors={ this.state.fixableClaimErrors }
                    onClose={this._handleCancel}
                    onEdit={ () => {
                        this.setState({
                            fixableClaimErrors: [],
                            currentPage: 0
                        });
                    }}
                    onSave={() => this._submitClaim(true) }
                    loader={this.props.loader}
                />
            );
        }

        if (this.state.showSuccess || this.state.showHSA) {
            if (this.state.showSuccess) {
                return (
                    <ClaimSuccessModal
                        amountClaimed={Number(this.state.amountClaimed) || 0}
                        patient={this.state.meta.patient.name}
                        onClose={this.props.close}
                        uid={ this.state.uid }
                    />
                );
            }

            return (
                <HealthSpendingModal
                    amountClaimed={Number(this.state.amountClaimed)}
                    amountSubmitted={Number(this.state.amountSubmitted)}
                    patient={this.state.meta.patient.name}
                    completeClaim={() => this._showSuccess(false)}
                    useHSA={this._useHSA}
                    healthSpendRemaining={this.props.healthSpendRemaining}
                    isProcessingTopOff={this.state.isProcessingTopOff}
                    onClose={this.props.close}
                    loader={this.props.loader}
                />
            );
        }

        if (this.state.showClaimProcessingScreen) {
            return (
                <ClaimProcessingModal />
            );
        }

        let title = i18n.t(tokens.titles["submit claim"]);

        if (this.props.type === 'HealthSpending') {
            title = i18n.t(tokens.labels.claims.submitHealthSpendingClaim);
        } else if (this.props.type === 'Lifestyle') {
            title = i18n.t(tokens.labels.claims.submitLifestyleSpendingClaim);
        }

        return (
            <div>
                {
                    this.props.loader.isLoading && <Loader/>
                }

                {
                    this.state.currentPage === 0 &&
                        <MetaClaimInputModal
                            meta={ this.state.meta }
                            saveData={ this._saveDataInState }
                            next={ this._next }
                            type={ this.props.type }
                            title={title}
                            onBack={ this.state.currentPage === 0 ? null : this._previous}
                            onClose={ this._handleCancel }
                        />
                }

                {
                    this.state.currentPage === 1 &&
                        <SubmitReceiptModal
                            receiptPhoto={ this.state.receiptPhoto }
                            saveData={ this._saveDataInState }
                            next={ this._next }
                            type={ this.props.type }
                            title={title}
                            onBack={ this.state.currentPage === 0 ? null : this._previous}
                            onClose={ this._handleCancel }
                        />
                }

                {
                    this.state.currentPage === 2 &&
                        <AddSupportingDocsModal
                            supportingPhotos={this.state.supportingPhotos}
                            saveData={ this._saveDataInState }
                            next={ this._next }
                            type={ this.props.type }
                            title={title}
                            onBack={ this.state.currentPage === 0 ? null : this._previous}
                            onClose={ this._handleCancel }
                        />
                }

                {
                    (this.state.currentPage === 3) &&
                        <ReviewClaimModal
                            meta={ this.state.meta }
                            receiptPhoto={ this.state.receiptPhoto }
                            supportingPhotos={ this.state.supportingPhotos }
                            submitClaim={ () => this._submitClaim(false) }
                            type={ this.props.type }
                            title={title}
                            onBack={ this.state.currentPage === 0 ? null : this._previous}
                            onClose={ this._handleCancel }
                        />
                }

                <span id="submitclaimform" />
            </div>
        );
    }
}

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    healthSpendRemaining: state.spendingAccounts.health.remaining,
    coverage: state.coverage.data,
    meta: state.user.meta,
    whitelabel: state.whitelabel
});

export default connect(mapStateToProps)(SubmitClaim);